import { map, switchMap } from "rxjs/operators";
import { environment as env } from "../../../environments/environment";

import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, SkipSelf } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Observable } from "rxjs";

import { NSDomainData, NSFormFields, NSHttpClient } from "../types";
import { FileRequestsService } from "./file-requests.service";
import { LocalAssetRequestService } from "./local-asset-requests.service";
import { RequestService } from "./request.service";
import { ResponseService } from "./response.service";

import { PERMISSION_STATE_PROP } from "../../+authenticated/+product-detail/state-props";
import { UserRoleService } from "../auth/user-role-service";
import { FormPostMessageActionsDirective } from "../components/global-message/form-post-message/actions.directive";
import { productVersionsCreateMutation, productVersionsUpdateMutation } from "./mutations/product-version.mutation";
import { castListQuery } from "./queries/cast.query";
import { crewListQuery } from "./queries/crew.query";
import { episodeListQuery } from "./queries/episodes.query";
import { productVersionAttachmentListQuery, productVersionDetailQuery, productVersionListQuery } from "./queries/product-version.query";
import { productListQuery, productMetadataQuery, productSynopsesQuery, seriesMetadataQuery } from "./queries/product.query";
import { userPermissionQuery } from "./queries/userPermission.query";

@Injectable()
export class ProductRequestService extends RequestService {
    user: any;
    doNotRepurposeVersionPermission = "product.feature.edited_version.dnr_version";

    constructor(
        private apollo: Apollo,
        private http: HttpClient,
        private response: ResponseService,
        private files: FileRequestsService,
        private localAsset: LocalAssetRequestService,
        public userRoleService: UserRoleService,
        @Inject(PERMISSION_STATE_PROP) public permissionProp,
        @SkipSelf() private onSaveActions: FormPostMessageActionsDirective,
    ) {
        super();
    }

    public getProducts(params?): Observable<NSHttpClient.APIResponse<NSDomainData.IProduct[]>> {
        params.searchText = params.searchText || "";
        return this.apollo
            .query((productListQuery(params)) as any).pipe(
                map(this.response.formatResponseBody));
    }

    public getProduct(productId: string): Observable<NSHttpClient.APIResponse<NSDomainData.IProduct>> {
        return this.getProducts().pipe(
            map((payload) => {
                const matchingProducts = payload.data.filter((row) => row.id.toString() === productId.toString());
                const product = {data: {}};
                if (!!matchingProducts) {
                    product.data = matchingProducts[0];
                }
                return product;
            }));
    }

    public getEpisodes(params?): Observable<NSHttpClient.APIResponse<NSDomainData.IEpisode[]>> {
        return this.apollo
            .query((episodeListQuery(params)) as any).pipe(
                map(this.response.formatResponseBody),
                map((res) => ({data: res.data.episodes})));
    }

    public getProductVersions(params?): Observable<NSHttpClient.APIResponse<NSDomainData.IProductVersion[]>> {
        return this.apollo
            .query((productVersionListQuery(params)) as any).pipe(
                map(this.response.formatResponseBody),
                map((res) => {
                    const data = [...res.data];
                    return {data};
                }));
    }

    public getEpisodeProductVersions(params): Observable<NSHttpClient.APIResponse<NSDomainData.IProductVersion[]>> {
        const formattedParams = {...params, productId: params.id};
        delete formattedParams.id;
        return this.getProductVersions(formattedParams);
    }

    public getAmxtVersionsAvailableBool(params): Observable<NSHttpClient.APIResponse<NSDomainData.IProductVersion[]>> {
        return this.getEpisodeProductVersions(params).pipe(
            map((res) => {
                const data = {isAmxtAvailable: res.data.some((datum) => !!datum.amxt)};
                return {data};
            }));
    }

    public getProductVersionColors(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/product-version-colors.json");
    }

    public getProductRatings(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/rating.json");
    }

    public getProductVersionTimings(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/product-version-timings.json");
    }

    public getProductVersionVideoStandards(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/product-version-video-standards.json");
    }

    public getProductVersionSourceTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/product-version-source-types.json");
    }

    public getProductVersionIdTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/product-version-id-types.json");
    }

    public getProductVersionOutputFileTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/product-version-output-file-types.json");
    }

    public getProductVersionCannotRepurposeReasons(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/cannot-repurpose-reasons.json");
    }

    public getMpaaRuntimes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption>> {
        return this.localAsset.getJsonFile("/assets/data/product/mpaa-runtimes.json");
    }

    public mutateProductVersion(payload, detailId?) {
        this.user = this.userRoleService.fullUser();
        if (payload.id && payload.initialDnr && payload.initialDnr !== payload.cannotRepurpose) {
            delete payload.initialDnr;
            return this.getUserPermission(this.doNotRepurposeVersionPermission)
                .pipe(switchMap((response) => {
                    return this.continueMutateProductVersion(payload, detailId)
                }));
        } else {
            delete payload.initialDnr;
            return this.continueMutateProductVersion(payload, detailId);
        }
    }

    public continueMutateProductVersion(payload, detailId?) {
        const audioConfigs = payload.productVersionAudioChannelConfigs.filter((channel) => {
            return !!channel && (!!channel.language || !!channel.position);
        });

        payload.productVersionAudioChannelConfigs = audioConfigs;
        payload.hasAD = (payload.hasAD === 1);

        if (!!detailId) {
            return this.apollo
                .mutate(productVersionsUpdateMutation(payload, detailId)).pipe(
                    map(this.response.formatResponseBody));
        } else {
            return this.apollo
                .mutate(productVersionsCreateMutation(payload)).pipe(
                    map(this.response.formatResponseBody));
        }
    }

    public getProductVersion(params): Observable<NSHttpClient.APIResponse<NSDomainData.IProductVersion>> {
        return this.apollo
            .query((productVersionDetailQuery(params)) as any).pipe(
                map(this.response.formatResponseBody));
    }

    public getCast(params?): Observable<NSHttpClient.APIResponse<any>> {
        return this.apollo
            .query((castListQuery(params)) as any).pipe(
                map(this.response.formatResponseBody),
                map((res) => ({data: res.data.cast})));
    }

    public getCrew(params?): Observable<NSHttpClient.APIResponse<any>> {
        return this.apollo
            .query((crewListQuery(params)) as any).pipe(
                map(this.response.formatResponseBody),
                map((res) => ({data: res.data.crew})));
    }

    public getProductVersionAttachments(params?): Observable<NSHttpClient.APIResponse<any>> {
        return this.files.getFiles(productVersionAttachmentListQuery, params);
    }

    public postProductVersionAttachments(body): Observable<any> {
        return this.files.postFiles(body, "productVersions");
    }

    public deleteProductVersionAttachment(id): Observable<any> {
        return this.files.deleteFile(id, "productVersions");
    }

    public getProductSynopses(params): Observable<NSHttpClient.APIResponse<any>> {
        return this.apollo
            .query((productSynopsesQuery(params)) as any).pipe(
                map(this.response.formatResponseBody),
                map((res) => {
                    const source = !!res.data.series ? res.data.series : res.data;
                    return {
                        data: {
                            shortSynopsis: source.shortSynopsis,
                            mediumSynopsis: source.mediumSynopsis,
                            fullSynopsis: source.fullSynopsis,
                        },
                    };
                }));
    }

    public getProductMetadata(params): Observable<NSHttpClient.APIResponse<any>> {
        return this.apollo
            .query((productMetadataQuery(params)) as any).pipe(
                map(this.response.formatResponseBody));
    }

    public getSeriesMetadata(params): Observable<NSHttpClient.APIResponse<any>> {
        return this.apollo
            .query((seriesMetadataQuery(params)) as any).pipe(
                map(this.response.formatResponseBody),
                map((res) => ({data: res.data.series})));
    }

    public getProductVersionAttachmentList(productVersionId): Observable<any> {
        const url = `${env.apiPath}/files/productVersions/${productVersionId}/attachments`;

        return this.http.get(url);
    }

    public updateProductVersionAttachment(attachmentId, docType): void {
        const url = `${env.apiPath}/files/productVersions/${attachmentId}?docType=${docType}`;

        this.http.put(url, {}).subscribe((response) => {
        });
    }

    public getUserPermission(permission): Observable<NSHttpClient.APIResponse<NSDomainData.IUserPermission>> {
        return this.apollo
            .query((userPermissionQuery(permission)) as any).pipe(
                map(this.response.formatResponseBody));
    }
}

export const productRequestProvider = {
    provide: RequestService,
    useClass: ProductRequestService,
    multi: true,
};
